import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  ListItem,
  CircularProgress,
} from "@mui/material";
import _ from "lodash";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchField from "src/components/shared/atoms/SearchField";
import { useLazyQuery } from "@apollo/client";
import GET_USER from "src/graphql/query/users/getUser";
import { enqueueSnackbar } from "notistack";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import Progress from "src/components/shared/atoms/circularProgress/CircularProgress";
import useStyles from "./Styles";

interface DeleteAgentsProps {
  reducedCounts: number;
  currentCount: number;
  selectedCount: number;
  handleDeleteAgentsData: any;
  handleCancel: any;
  loading: boolean;
}

const DeleteAgents: React.FC<DeleteAgentsProps> = ({
  reducedCounts,
  currentCount,
  selectedCount,
  handleDeleteAgentsData,
  handleCancel,
  loading,
}) => {
  const [inviteList, setInviteList] = useState<any[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<any[]>([]);
  const [showError, setShowError] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [searchListAgents, setSearchListAgents] = useState<any[]>([]);
  const [listAgents, setListAgents] = useState<any[]>([]);
  const {
    mainSuperAdminId,
    panelType,
    agentId: userId,
  } = useActiveUserDetail();
  const [pagination, setPagination] = useState({
    page: 0,
    hasNextPage: false,
    limit: 10,
  });
  const [searchListAgentsFlag, setSearchListAgentsFlag] = useState(false);

  const CURSOR = 0;

  const fetchUsers = (data: any) => {
    const rawData = data.users
      .filter((user: any) => user.id !== userId)
      .map((user: any) => ({
        ...user,
        isSelected: false,
      }));

    // const selectedIds = [
    //   ...selectedAgentIds?.map((agentId) => agentId.id),
    //   ...inviteList?.map((agentId: { id: any }) => agentId.id),
    // ];
    const selectedIds = [
      ...(selectedAgentIds?.map((agentId) => agentId.id) ?? []),
      ...(inviteList?.map((agentId: { id: any }) => agentId.id) ?? []),
    ];

    const prevData = rawData.map((item: { id: any }) => {
      if (selectedIds.includes(item.id)) {
        return {
          ...item,
          isSelected: true,
        };
      }
      return item;
    });

    if (!searchListAgentsFlag) {
      setListAgents((prev) => [...prev, ...prevData]);
    } else {
      setSearchListAgents((prev) => [...prev, ...prevData]);
    }

    setPagination({
      page: pagination.page + 1,
      limit: 10,
      hasNextPage: data.hasNextPage,
    });
  };

  const [
    getAgents,
    {
      loading: getAgentsQueryLoading,
      error: getAgentsQueryError,
      data: getAgentsQueryResult,
    },
  ] = useLazyQuery(GET_USER, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (getAgentsQueryError) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  }, [getAgentsQueryError]);

  useEffect(() => {
    if (getAgentsQueryResult && getAgentsQueryResult?.getUsers?.users) {
      fetchUsers(getAgentsQueryResult.getUsers);
    }
  }, [getAgentsQueryResult]);

  const loadMoreDataHandle = (i: React.SyntheticEvent) => {
    const bottom =
      (i.target as HTMLElement).scrollHeight -
        (i.target as HTMLElement).clientHeight -
        (i.target as HTMLElement).scrollTop <
      1;
    if (bottom && pagination.hasNextPage) {
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor:
            pagination.page === CURSOR
              ? pagination.limit * pagination.page
              : Number(listAgents.at(-1).id),
          limit: pagination.limit,
          query: "",
          panelType,
        },
      });
    }
  };

  useEffect(() => {
    getAgents({
      variables: {
        mainSuperAdminId,
        cursor: CURSOR,
        limit: pagination.limit,
        query: "",
        panelType,
      },
    });
  }, []);

  const handleSelectAll = (flag: boolean) => {
    const list = searchListAgentsFlag ? [...searchListAgents] : [...listAgents];
    list.forEach((element) => {
      element.isSelected = flag;
    });
    if (!searchListAgentsFlag) {
      setListAgents(list);
    } else {
      setSearchListAgents(list);
    }
    setAllSelected(flag);
    setSelectedAgentIds(flag ? list : []);
  };

  const handleCheckListItem = (item_: any, flag: boolean) => {
    const newArr = _.map(listAgents, function (a) {
      return a.id === item_.id ? { ...a, isSelected: !flag } : a;
    });
    setListAgents(newArr);
    const selectedIds = newArr.filter((item) => item.isSelected);

    const newArrSearch = _.map(searchListAgents, function (a) {
      return a.id === item_.id ? { ...a, isSelected: !flag } : a;
    });
    setSearchListAgents(newArrSearch);
    const selectedSearchIds = newArrSearch.filter((item) => item.isSelected);

    setSelectedAgentIds([...selectedIds, ...selectedSearchIds]);
  };

  const handleSubmit = () => {
    if (reducedCounts > 0) {
      if (selectedAgentIds.length !== reducedCounts) {
        setShowError(true);
        enqueueSnackbar(
          `Please select exactly ${reducedCounts} agent${
            reducedCounts > 1 ? "s" : ""
          } to remove.`,
          { variant: "error" },
        );
        return;
      }
    }

    const updatedList = [
      ...inviteList.filter(
        (item: { designation: { paneltype: string } }) =>
          item.designation.paneltype !== "AGENT",
      ),
      ...selectedAgentIds,
    ];

    setInviteList(updatedList);
    handleDeleteAgentsData(selectedAgentIds);
  };

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => {
        setShowError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showError]);

  const { classes } = useStyles();
  const [text, setText] = useState("");
  const [debounncedText, setDebouncedText] = useState("");

  const searchBarHandle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedText(text);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [text]);

  useEffect(() => {
    setSearchListAgents([]);
    if (debounncedText !== "") {
      setSearchListAgentsFlag(true);
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor: CURSOR,
          limit: pagination.limit,
          query: debounncedText,
          panelType,
        },
      });
    } else {
      setSearchListAgentsFlag(false);
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor: CURSOR,
          limit: pagination.limit,
          query: "",
          panelType,
        },
      });
    }
  }, [debounncedText]);

  const searchFieldProps = {
    value: text,
    onChange: searchBarHandle,
    placeholder: "search",
    fieldClass: classes.fieldStyle,
    showEndIcon: true,
    endIcon: getAgentsQueryLoading && (
      <CircularProgress size={20} sx={{ marginRight: "10px" }} />
    ),
  };

  return (
    <Box>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Instructional message */}
        {reducedCounts > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error">
              You currently have <strong>{currentCount}</strong> agents, but you
              chose to downgrade to <strong>{selectedCount}</strong> agents.
              Please remove <strong>{reducedCounts}</strong> agent
              {reducedCounts > 1 ? "s" : ""} from the list.
            </Typography>
          </Box>
        )}

        {/* Chat handover info */}
        <Box className={classes.chatHandoverInfoBox}>
          <Typography className={classes.chatHandoverText}>
            Active conversations associated with deleted agents will be
            automatically reassigned to available agents upon receiving new
            messages.
          </Typography>
        </Box>

        {/* info message for the deleting invited-users */}
        <Box className={classes.delInviUsersInfoBox}>
          <ErrorOutlineIcon className={classes.infoErrorIcon} />
          <Typography className={classes.currentInfoTextBox}>
            Invited users will be permanently deleted and can only be restored
            by sending them a new invitation.
          </Typography>
        </Box>

        <SearchField {...searchFieldProps} />

        <Box sx={{ marginTop: showError ? "-8px" : "10px" }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => handleSelectAll(!allSelected)}
          />
          <Box component="span" className={classes.selectAll}>
            Select all
          </Box>
        </Box>

        <Box onScroll={loadMoreDataHandle} className={classes.loadMoreData}>
          {!getAgentsQueryLoading && listAgents.length === 0 && (
            <div className="m-auto text-center">
              <p className="font-small">No record found</p>
            </div>
          )}

          {!searchListAgentsFlag &&
            listAgents.map((item_) => (
              <ListItem
                alignItems="center"
                sx={{ padding: "0px" }}
                key={item_.id}
              >
                <input
                  checked={item_.isSelected}
                  onChange={() => handleCheckListItem(item_, item_.isSelected)}
                  type="checkbox"
                />
                <Avatar
                  alt="John Doe"
                  className={classes.image}
                  src={item_.picture}
                />
                <Typography variant="subtitle2" className={classes.name}>
                  {item_.name}
                </Typography>
              </ListItem>
            ))}

          {searchListAgentsFlag &&
            searchListAgents.map((item_) => (
              <ListItem
                alignItems="center"
                sx={{ padding: "0px" }}
                key={item_.id}
              >
                <input
                  checked={item_.isSelected}
                  onChange={() => handleCheckListItem(item_, item_.isSelected)}
                  type="checkbox"
                />
                <Avatar
                  alt="John Doe"
                  className={classes.image}
                  src={item_.picture}
                />
                <Typography variant="subtitle2" className={classes.name}>
                  {item_.name}
                </Typography>
              </ListItem>
            ))}

          {!debounncedText && getAgentsQueryLoading && <Progress />}
        </Box>

        {/* Error message */}
        {showError && (
          <Typography variant="caption" color="error">
            You still need to choose {reducedCounts - selectedAgentIds.length}{" "}
            more agent
            {reducedCounts - selectedAgentIds.length > 1 ? "s" : ""}.
          </Typography>
        )}

        <Box className={classes.submit}>
          <Button
            variant="contained"
            className={classes.cancelBtn}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={
              loading ||
              (reducedCounts > 0
                ? selectedAgentIds.length !== reducedCounts
                : !(selectedAgentIds.length > 0))
            }
            className={classes.submitButton}
          >
            {loading ? (
              <span>
                Deleting...{" "}
                <CircularProgress size={13} sx={{ color: "white" }} />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DeleteAgents;
